/**
 * RegistrationDecisionDialogs.tsx - the inline forms of the insurer's answer on a registration row
 * (Eintragungsbestätigung, Stefan P5 V2.0 steps 25 and 28): «Bestätigen» (Wirkungsdatum, Brokercode,
 * ausgenommene Policen, Beleg), «Ablehnen» (Grund), «Rückfrage» (Notiz), «Korrektur» (Grund, Notiz). On
 * an Auflösungsmeldung «Ablehnen» is «Austragung verweigert» (Bemerkung, Beleg): no P5 reason.
 *
 * The panel decides which button a row shows (row.actions + mandate.eingangskontrolle) and runs the
 * mutation; a form only collects the body. A manual confirmation needs a Beleg, so «Bestätigen» stays
 * disabled without one; «Ablehnen» may carry one. The Beleg is picked from the registration's mails
 * (GET /registrations/:regId/beleg-options, newest first, paged), each mail's stored attachments and the
 * customer's documents (registration-beleg.ts).
 */

import { useState } from 'react';
import {
  REGISTRATION_KORREKTUR_REASON_LABEL,
  REGISTRATION_KORREKTUR_REASON_VALUES,
  REGISTRATION_REJECTION_REASON_LABEL,
  REGISTRATION_REJECTION_REASON_VALUES,
} from '@brokly/shared';
import type {
  AblehnenRegistrationBody,
  BestaetigenRegistrationBody,
  RegistrationDto,
  RegistrationKorrekturReason,
  RegistrationRejectionReason,
} from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import type { RegistrationDecision } from './mandate-registration-queries';
import { pickedBeleg, useRegistrationBelege } from './registration-beleg';
import { RegistrationBelegSelect } from './RegistrationBelegSelect';

/**
 * «Wiedereintragung bestätigen»: a Brokermandat row the insurer de-registered (ausgetragen) after a
 * termination that was then revoked. The api only records the Beleg (no Wirkungsdatum, Brokercode or
 * exclusions), so the form asks for nothing else.
 */
export function isWiedereintragung(row: Pick<RegistrationDto, 'prozess' | 'status'>): boolean {
  return row.prozess === 'p5_broker' && row.status === 'ausgetragen';
}

/** The answer decisions that open a form (Rückfrage beantwortet is one click). */
export type RegistrationAnswerFormKind = 'bestaetigen' | 'ablehnen' | 'rueckfrage' | 'korrektur';

interface FormProps {
  row: RegistrationDto;
  subjectType: 'person' | 'firma';
  subjectId: string;
  pending: boolean;
  onSubmit: (decision: RegistrationDecision) => void;
  onCancel: () => void;
}

const INPUT = 'rounded border px-2 py-1 text-sm';

function Cancel({ onCancel }: { onCancel: () => void }) {
  return (
    <Button size="sm" variant="ghost" onClick={onCancel}>
      Abbrechen
    </Button>
  );
}

function BestaetigenForm({ row, subjectId, pending, onSubmit, onCancel }: FormProps) {
  const [effectiveDate, setEffectiveDate] = useState(row.requested_effective_date?.slice(0, 10) ?? '');
  const [brokerVuCode, setBrokerVuCode] = useState(row.broker_vu_code ?? '');
  const [excluded, setExcluded] = useState<string[]>([]);
  const [notiz, setNotiz] = useState('');
  const [belegId, setBelegId] = useState('');
  // an Austragung (Auflösungsmeldung): its date and a remark, no Brokercode, no excluded policies
  const austragung = row.prozess === 'p6_aufloesung';
  // a Wiedereintragung: the Beleg only
  const wieder = isWiedereintragung(row);
  const belege = useRegistrationBelege(row.id, subjectId);
  const beleg = pickedBeleg(belege, belegId);
  const hasBeleg = beleg.documentId !== undefined || beleg.communicationId !== undefined;

  function toggle(policyId: string) {
    setExcluded((cur) => (cur.includes(policyId) ? cur.filter((x) => x !== policyId) : [...cur, policyId]));
  }

  function submit() {
    if (wieder) {
      const only: BestaetigenRegistrationBody = {};
      if (beleg.documentId !== undefined) only.documentId = beleg.documentId;
      if (beleg.communicationId !== undefined) only.communicationId = beleg.communicationId;
      onSubmit({ action: 'bestaetigen', registrationId: row.id, body: only });
      return;
    }
    const body: BestaetigenRegistrationBody = austragung ? {} : { excludedPolicyIds: excluded };
    if (effectiveDate !== '') body.effectiveDate = effectiveDate;
    if (!austragung && brokerVuCode.trim() !== '') body.brokerVuCode = brokerVuCode.trim();
    if (austragung && notiz.trim() !== '') body.notiz = notiz.trim();
    if (beleg.documentId !== undefined) body.documentId = beleg.documentId;
    if (beleg.communicationId !== undefined) body.communicationId = beleg.communicationId;
    onSubmit({ action: 'bestaetigen', registrationId: row.id, body });
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/30 p-2" data-testid="reg-bestaetigen-form">
      {wieder && <p className="text-xs text-muted-foreground">Der Versicherer hat den Broker wieder eingetragen: bitte den Beleg wählen.</p>}
      {!wieder && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            {austragung ? 'Ausgetragen ab' : 'Wirkungsdatum'}
            <input
              type="date"
              className={INPUT}
              aria-label={austragung ? 'Ausgetragen ab' : 'Wirkungsdatum'}
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </label>
          {austragung ? (
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              Bemerkung
              <input className={INPUT} aria-label="Bemerkung" value={notiz} onChange={(e) => setNotiz(e.target.value)} />
            </label>
          ) : (
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              Brokercode
              <input className={INPUT} aria-label="Brokercode" value={brokerVuCode} onChange={(e) => setBrokerVuCode(e.target.value)} />
            </label>
          )}
        </div>
      )}
      {!austragung && !wieder && row.policies.length > 0 && (
        <fieldset className="flex flex-wrap items-center gap-3 text-xs">
          <legend className="mb-1 text-muted-foreground">Vom Versicherer ausgenommene Policen</legend>
          {row.policies.map((p) => {
            const nr = p.policy_number ?? 'ohne Nummer';
            return (
              <label key={p.policy_id} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  aria-label={`Police ${nr} ausnehmen`}
                  checked={excluded.includes(p.policy_id)}
                  onChange={() => toggle(p.policy_id)}
                />
                {nr}
              </label>
            );
          })}
        </fieldset>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <RegistrationBelegSelect belege={belege} value={belegId} onChange={setBelegId} placeholder="Beleg wählen" />
      </div>
      {!hasBeleg && <p className="text-xs text-muted-foreground">Eine manuelle Bestätigung braucht einen Beleg.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={pending || !hasBeleg} onClick={submit} data-testid="reg-bestaetigen-confirm">
          {austragung ? 'Austragung bestätigen' : wieder ? 'Wiedereintragung bestätigen' : 'Bestätigen'}
        </Button>
        <Cancel onCancel={onCancel} />
      </div>
    </div>
  );
}

/** «Austragung verweigert» (an Auflösungsmeldung): what the insurer said and the Beleg; no P5 reason. */
function AustragungVerweigertForm({ row, subjectId, pending, onSubmit, onCancel }: FormProps) {
  const [notiz, setNotiz] = useState('');
  const [belegId, setBelegId] = useState('');
  const belege = useRegistrationBelege(row.id, subjectId);
  const beleg = pickedBeleg(belege, belegId);
  function submit() {
    if (notiz.trim() === '') return;
    const body: AblehnenRegistrationBody = { notiz: notiz.trim(), ...beleg };
    onSubmit({ action: 'ablehnen', registrationId: row.id, body });
  }
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="reg-verweigert-form">
      <input
        className={`${INPUT} min-w-48 flex-1`}
        placeholder="Was sagt der Versicherer?"
        aria-label="Bemerkung"
        value={notiz}
        onChange={(e) => setNotiz(e.target.value)}
      />
      <RegistrationBelegSelect belege={belege} value={belegId} onChange={setBelegId} placeholder="Beleg (optional)" />
      <Button size="sm" disabled={pending || notiz.trim() === ''} onClick={submit} data-testid="reg-ablehnen-confirm">
        Austragung verweigert
      </Button>
      <Cancel onCancel={onCancel} />
    </div>
  );
}

function AblehnenForm(props: FormProps) {
  if (props.row.prozess === 'p6_aufloesung') return <AustragungVerweigertForm {...props} />;
  return <RegistrationAblehnenForm {...props} />;
}

function RegistrationAblehnenForm({ row, subjectId, pending, onSubmit, onCancel }: FormProps) {
  const [reason, setReason] = useState<RegistrationRejectionReason | ''>('');
  const [belegId, setBelegId] = useState('');
  const belege = useRegistrationBelege(row.id, subjectId);
  const beleg = pickedBeleg(belege, belegId);
  function submit() {
    if (reason === '') return;
    const body: AblehnenRegistrationBody = { reasonCode: reason, ...beleg };
    onSubmit({ action: 'ablehnen', registrationId: row.id, body });
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select className={INPUT} aria-label="Grund" value={reason} onChange={(e) => setReason(e.target.value as RegistrationRejectionReason | '')}>
        <option value="">Grund wählen</option>
        {REGISTRATION_REJECTION_REASON_VALUES.map((v) => (
          <option key={v} value={v}>
            {REGISTRATION_REJECTION_REASON_LABEL[v]}
          </option>
        ))}
      </select>
      <RegistrationBelegSelect belege={belege} value={belegId} onChange={setBelegId} placeholder="Beleg (optional)" />
      <Button size="sm" disabled={pending || reason === ''} onClick={submit} data-testid="reg-ablehnen-confirm">
        Ablehnen
      </Button>
      <Cancel onCancel={onCancel} />
    </div>
  );
}

function RueckfrageForm({ row, pending, onSubmit, onCancel }: FormProps) {
  const [notiz, setNotiz] = useState('');
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        className={`${INPUT} min-w-48 flex-1`}
        placeholder="Was fragt der Versicherer?"
        aria-label="Notiz"
        value={notiz}
        onChange={(e) => setNotiz(e.target.value)}
      />
      <Button
        size="sm"
        disabled={pending || notiz.trim() === ''}
        onClick={() => onSubmit({ action: 'rueckfrage', registrationId: row.id, body: { notiz: notiz.trim() } })}
        data-testid="reg-rueckfrage-confirm"
      >
        Rückfrage erfassen
      </Button>
      <Cancel onCancel={onCancel} />
    </div>
  );
}

function KorrekturForm({ row, pending, onSubmit, onCancel }: FormProps) {
  const [reason, setReason] = useState<RegistrationKorrekturReason | ''>('');
  const [notiz, setNotiz] = useState('');
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select className={INPUT} aria-label="Grund" value={reason} onChange={(e) => setReason(e.target.value as RegistrationKorrekturReason | '')}>
        <option value="">Grund wählen</option>
        {REGISTRATION_KORREKTUR_REASON_VALUES.map((v) => (
          <option key={v} value={v}>
            {REGISTRATION_KORREKTUR_REASON_LABEL[v]}
          </option>
        ))}
      </select>
      <input className={`${INPUT} min-w-48 flex-1`} placeholder="Notiz" aria-label="Notiz" value={notiz} onChange={(e) => setNotiz(e.target.value)} />
      <Button
        size="sm"
        disabled={pending || reason === ''}
        onClick={() =>
          reason !== '' &&
          onSubmit({
            action: 'korrektur',
            registrationId: row.id,
            body: { reasonCode: reason, ...(notiz.trim() !== '' && { notiz: notiz.trim() }) },
          })
        }
        data-testid="reg-korrektur-confirm"
      >
        Korrektur
      </Button>
      <Cancel onCancel={onCancel} />
    </div>
  );
}

/** The inline form of one answer decision on a registration row. */
export function RegistrationAnswerForm({ kind, ...props }: FormProps & { kind: RegistrationAnswerFormKind }) {
  switch (kind) {
    case 'bestaetigen':
      return <BestaetigenForm {...props} />;
    case 'ablehnen':
      return <AblehnenForm {...props} />;
    case 'rueckfrage':
      return <RueckfrageForm {...props} />;
    case 'korrektur':
      return <KorrekturForm {...props} />;
    default: {
      const unhandled: never = kind;
      throw new Error(`unhandled answer form ${String(unhandled)}`);
    }
  }
}
